import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ShellAdapter, ShellProvider } from "@agentnative/shell";
import { ThemeProvider } from "@agentnative/ui";
import { router } from "./router";
import { consoleShellContract } from "./shellContract";
import { useLoadCurrentOrgMemberships } from "./stores/currentOrg";
import { useAuthStore } from "./stores/auth";
import { refresh } from "./lib/api/auth";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 1;
      },
    },
  },
});

// Loads the logged-in user's real organization memberships as soon as
// they're known, feeding useCurrentOrgStore -- see
// stores/currentOrg.ts's doc comment. Rendered inside
// QueryClientProvider since it uses useQuery.
function CurrentOrgLoader() {
  useLoadCurrentOrgMemberships();
  return null;
}

// Runs once at startup to answer "am I logged in?" from the
// refresh-token cookie alone -- without this, useAuthStore.user starts
// `null` on every fresh page load (it is deliberately not persisted to
// localStorage, see stores/auth.ts's doc comment) even when the
// browser still holds a perfectly valid session cookie, so a reload
// (or simply opening a new tab) looked logged-out until this ran.
// There is no separate GetCurrentUser/WhoAmI endpoint -- POST
// /auth/refresh doubles as this check (see lib/api/auth.ts's refresh()
// doc and auth.proto's RefreshTokenResponse.User field). A 401 (no
// cookie / expired / revoked session) is the ordinary logged-out case,
// not an error -- caught and treated the same as a false `success`.
function SessionBootstrap() {
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);

  useEffect(() => {
    let cancelled = false;
    refresh()
      .then((resp) => {
        if (cancelled) return;
        if (resp.success && resp.user) {
          setUser(resp.user);
        } else {
          clear();
        }
      })
      .catch(() => {
        if (!cancelled) clear();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setUser/clear are stable zustand setters; this must run exactly once at startup
  }, []);

  return null;
}

function ConsoleApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionBootstrap />
      <CurrentOrgLoader />
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

// Wrap with ShellAdapter so this app can slot into the marketplace shell
// later without a rewrite (see ui/ui_prd.md "Deployment Modes"). Today
// there is no marketplace consumer, so this only buys us the shell
// contract's ThemeProvider wiring and lifecycle hooks -- no over-engineered
// marketplace-mode branching.
const WrappedConsoleApp = ShellAdapter.wrap(ConsoleApp, consoleShellContract);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      <ShellProvider>
        <WrappedConsoleApp />
      </ShellProvider>
    </ThemeProvider>
  </StrictMode>,
);
